<template>
    <section 
        class="slider" 
        aria-roledescription="carousel"
        aria-label="Highlighted photos"
    >
        <div class="slider__controls" @keydown.tab="stopPlay">
            <button 
                class="link link--play"
                type="button" 
                :aria-label="isPlaying ? 'Stop automatic slide show' : 'Play automatic slide show'"
                @click="isPlaying ? stopPlay() : autoPlay()"
            >
                <span>
                    <svg v-if="isPlaying" role="presentation" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 70 70" width="64px" height="64px">
                        <path d="M52.5,0c-4.972,0-9,1.529-9,6.5v57c0,4.971,4.028,6.5,9,6.5c4.971,0,9-1.529,9-6.5v-57     C61.5,1.529,57.471,0,52.5,0z" fill="#FFFFFF"/>
                        <path d="M17.5,0c-4.972,0-9,1.529-9,6.5v57c0,4.971,4.028,6.5,9,6.5c4.971,0,9-1.529,9-6.5v-57     C26.5,1.529,22.471,0,17.5,0z" fill="#FFFFFF"/>
                    </svg>
                    <svg v-else role="presentation" focusable="false" xmlns="http://www.w3.org/2000/svg" width="64px" height="64px" viewBox="0 0 373.008 373.008">
                        <path d="M61.792,2.588C64.771,0.864,68.105,0,71.444,0c3.33,0,6.663,0.864,9.655,2.588l230.116,167.2    c5.963,3.445,9.656,9.823,9.656,16.719c0,6.895-3.683,13.272-9.656,16.713L81.099,370.427c-5.972,3.441-13.334,3.441-19.302,0    c-5.973-3.453-9.66-9.833-9.66-16.724V19.305C52.137,12.413,55.818,6.036,61.792,2.588z" fill="#fff"/>
                    </svg>
                </span>
            </button>
        </div>
        <div class="slider__wrap">
            <div 
                class="slider__selected" 
                ref="slider"
                id="slides" 
                :aria-live="isPlaying ? 'off' : 'polite'" 
            >
                <circle-box
                    v-for="(circle, c) in images"
                    :class="{ 'is-active': current == c }"
                    :key="`image-${c}`"
                    :slug="slug"
                    :simple="true"
                    :media="circle.media"
                    :alttext="''"

                    v-bind="tabpanelAttrs(c)"
                />
            </div>
        </div>
        <div 
            class="slider__thumbs" 
            role="tablist"
            aria-label="Slides"
            ref="thumbs" 
            tabindex="-1" 
            @mouseenter="stopPlay"
            @mouseleave="wasPlaying && autoPlay()"
            @keydown.left.right.prevent="onArrows"
        >
            <circle-box
                v-for="(circle, c) in images"
                :key="`thumb-${c}`"
                :slug="slug"
                :simple="true"
                :media="circle.media"
                :alttext="''"

                v-bind="thumbAttrs(c, current)"
                @click.native="$emit('slider-goto', c)"
            />
        </div>

    </section>
</template>

<script>
import { gsap } from 'gsap'
import CircleBox from './Circle'

// function isInViewport(element) {
//     const rect = element.getBoundingClientRect();
//     return (
//         rect.top >= 0 &&
//         rect.left >= 0 &&
//         rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
//         rect.right <= (window.innerWidth || document.documentElement.clientWidth)
//     );
// }

const sliderOpts = {
    methods: {
        tabpanelAttrs( c ) {
            return {
                id: `slide-i-${c}`,
                role: "tabpanel",
                "aria-roledescription": "slide",
                "aria-label": `${c + 1} of ${this.images.length}`,
            }
        },
        thumbAttrs( c, current ) {
            const isCurrent = current === c
            let opts = {
                tabindex: '-1',
                "aria-selected": 'false',
            }

            if (isCurrent) {
                opts = {
                    tabindex: '0',
                    class: 'is-active',
                    "aria-selected": 'true',
                }
            }

            return {
                ...opts,
                role: "tab",
                // tag: "button",
                'aria-label': `Slide ${c+1}`,
                'aria-controls': `slide-i-${c}`,
            }
        },
        focusSelectedThumb() {
            return setTimeout(() => {
                const { thumbs } = this.$refs
                const selected = thumbs.querySelector('[aria-selected=true]')
                this.wasPlaying && this.stopPlay()
                selected && selected.focus()
            }, 100)
        },
        onArrows({ code }) {
            if (code === "ArrowRight") {
                this.nextSlide()
            }
            else { 
                this.prevSlide()
            }

            this.focusSelectedThumb()
        },
        prevSlide() {
            if ( this.current > 0 ) {
                return this.current--
            }
            this.current = this.images.length - 1
        },
        nextSlide() {
            if ( this.current < this.images.length - 1 ) {
                return this.current++
            }
            this.current = 0
        },
    },
    watch: {
        isPlaying( play ) {
            if (play) {
                this.timeline = setInterval( () => {
                    this.wasPlaying = true
                    this.nextSlide()
                }, 5000)
                return;
            }

            this.timeline && 
            clearInterval(this.timeline)
        },
    },
}

export default {
    name: 'Slider',
    props: ['slug', 'images', 'id', 'moduleType'],
    mixins: [sliderOpts],
    components: {
        CircleBox
    },
    data() {
        return {
            wasPlaying: false,
            isPlaying: false,
            timeline: null,
            imgWidth: 590,
            current: 0
        }
    },
    beforeDestroy() {
        // this.$root.$off('hide-stories')
        // this.$root.$off('stories-shown')
        this.$off('slider-goto')
        this.stopPlay()
    },
    mounted() {
        this.$nextTick( function() {

            this.$on('slider-goto', x => {
                this.current = x
            })

            // this.$root.$on('hide-stories', () => {
            //     this.isPlaying && this.stopPlay()
            // })

            // this.$root.$on('stories-shown', () => {
            // if ( isInViewport(this.$el) ) 
            //     this.autoPlay()
            // else 
            //     this.stopPlay()
            // })
        })

        this.presetSize()
        
        this.autoPlay()
    },
    methods: {
        presetSize( delay = 100 ) {
            return setTimeout( () => {
                if ( this.$refs.slider ) {
                    this.imgWidth = this.$refs.slider.clientWidth
                    gsap.set( this.$refs.slider, { x: this.offset })
                }
            }, delay)
        },
        stopPlay() {
            this.isPlaying = false
        },
        autoPlay() {
            this.isPlaying = true
        }
    },
    watch: {
        current() {
            setTimeout( () => {
                gsap.to( this.$refs.slider, {
                    duration: 1.6,
                    x: this.offset,
                    ease: 'expo'
                })
            })
        },
        browserWidth() {
            this.presetSize(16)
        },
        currentStory(id) {
            if ( id === this.id ) {
                return this.autoPlay()
            }
            if ( this.timeline ) {
                return this.stopPlay()
            }
        },
    },
    computed: {
        offset() {
            return -this.imgWidth * this.current
        },
        browserWidth() {
            const { width } = this.$store.getters['browser/size']
            return width
        },
        currentStory() {
            return this.$store.getters[`${this.moduleType}/currentStory`]
        },
    }
}
</script>

<style lang="scss" scoped>
::v-deep .story--slider .slider {
    .story__body > :first-child {
        > :only-child {
            display: contents;
        }
    }
}
.link--play {
    --link-size: 36px;
    left: 20px; 
    top: 20px;
    position: absolute;
    z-index: 2;
}
.link--play:focus-visible > :only-child {
    outline: 2px dashed #fff;
    outline-offset: -5px;
}
.link--play > :only-child {
    margin-right: 0;

    svg {
        --link-size: 75%;
    }
}
@media (max-width: 767px) {
    .slider {
        padding-bottom: calc(100% + 88px);
        width: calc(100% + 40px);
        left: -20px;
        display: flex;

        &__thumbs,
        &__wrap {
            position: absolute;
        }
        &__thumbs {
            bottom: 0;
        }
    }
}

    .slider {
        .circle, .circle + .circle {
            background: none;
            margin-top: initial
        }
        &__selected .circle + .circle {
            max-width: 80px;//initial
        }
        &__thumbs .circle {
            -webkit-tap-highlight-color: transparent;

            &:nth-child(1) {
                margin-left: 5%;
                margin-top: -17%
            }
            &:nth-child(2) {
                margin-top: -9%
            }
            &:nth-child(3) {
                margin-left: 1%;
                margin-top: -4.5%
            }
            &:nth-child(4) {
                margin-left: 1%;
                margin-top: -5%
            }
            &:nth-child(5) {
                margin-left: 1%;
                margin-top: -9%
            }
            &:nth-child(6) {
                margin-top: -19%;
                margin-right: 5%
            }
        }
    }
</style>
